import React, { useState } from 'react';
import { Restaurant } from '../models/Restaurant';
import { setRestaurantFavorite, setRestaurantBlocked } from '../services/DataStore';
import { debug } from '../utils/debug';

const RestaurantView = ({ restaurant, onShowMap, isInfoWindow = false, onFavoriteChange, onBlock, isFavorite = false, meal }) => {
  const [localFavoriteState, setLocalFavoriteState] = useState(isFavorite);

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    debug.log('Favorite button clicked:', {
      restaurantId: restaurant.id,
      currentState: localFavoriteState,
      newState: !localFavoriteState
    });

    const newFavoriteState = !localFavoriteState;
    setLocalFavoriteState(newFavoriteState); // Optimistic update
    debug.log('Local state updated optimistically');

    try {
      debug.log('Calling setRestaurantFavorite with:', {
        restaurantId: restaurant.id,
        isFavorite: newFavoriteState
      });
      
      await setRestaurantFavorite(restaurant.id, newFavoriteState);
      debug.log('setRestaurantFavorite completed successfully');

      if (onFavoriteChange) {
        debug.log('Calling onFavoriteChange callback');
        onFavoriteChange(restaurant.id, newFavoriteState);
      }
    } catch (error) {
      debug.error('Error updating favorite:', error);
      setLocalFavoriteState(!newFavoriteState); // Revert on error
      debug.log('Reverted local state due to error');
    }
  };

  const handleBlockClick = async (e) => {
    e.stopPropagation();
    debug.log('Block button clicked:', {
      restaurantId: restaurant.id,
      meal: meal?.id
    });
    try {
      await setRestaurantBlocked(restaurant.id, true, meal);
      if (onBlock) {
        onBlock(restaurant.id);
      }
    } catch (error) {
      console.error('Error blocking restaurant:', error);
    }
  };

  if (!restaurant) return null;

  return (
    <div className={`relative w-[350px] h-[350px] ${isInfoWindow ? '' : 'rounded-lg overflow-hidden'} border-2 border-accent`}>
      {/* Background Image */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900">
        {restaurant.imageUrl ? (
          <img 
            src={restaurant.imageUrl} 
            alt={restaurant.name}
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
              e.target.parentElement.innerHTML = `
                <span class="material-symbols-outlined text-gray-400 text-6xl">restaurant</span>
              `;
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="material-symbols-outlined text-gray-400 text-6xl">restaurant</span>
          </div>
        )}
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />

      {/* Content Container */}
      <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col">
        {/* Restaurant Name */}
        <h2 className="text-white text-3xl font-bold mb-1">
          {restaurant.name}
        </h2>

        {/* Rating and Review Count */}
        <div className="flex items-center space-x-2 mb-0.5">
          <img 
            src={Restaurant.getRatingImage(restaurant.rating)}
            alt={`${restaurant.rating} stars`}
            className="h-6 w-auto"
          />
          <span className="text-white text-lg">
            ({restaurant.reviewCount})
          </span>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              window.open(restaurant.url, '_blank');
            }}
            className="w-6 h-6"
          >
            <img 
              src="/images/yelp_burst.svg"
              alt="Open in Yelp"
              className="w-full h-full"
            />
          </button>
        </div>

        {/* Price and Categories */}
        <div className="text-white text-base mb-0.5">
          {restaurant.price && (
            <span>{restaurant.price}</span>
          )}
          {restaurant.categories && restaurant.categories.length > 0 && (
            <span className="ml-2">
              {restaurant.categories.map(cat => cat.title).join(', ')}
            </span>
          )}
        </div>

        {/* Address and Phone */}
        <div className="space-y-0.25 mt-auto">
          {restaurant.location?.displayAddress && restaurant.location.displayAddress.length > 0 && (
            <div className="pr-16 -ml-2">
              <div 
                className="text-white text-base cursor-pointer hover:bg-white/10 rounded p-2 flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  if (restaurant.coordinates) {
                    const { latitude, longitude } = restaurant.coordinates;
                    const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                <span className="flex-1 font-bold">{restaurant.location.displayAddress[0]}</span>
                <span className="material-symbols-outlined text-white/80 ml-2">directions</span>
              </div>
            </div>
          )}
          
          {restaurant.phone && (
            <div className="pr-16 -ml-2">
              <a 
                href={`tel:${restaurant.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="text-white text-base cursor-pointer hover:bg-white/10 rounded p-2 flex items-center"
              >
                <span className="flex-1">{restaurant.phone}</span>
                <span className="material-symbols-outlined text-white/80 ml-2">phone</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
        <button 
          className="w-10 h-10 bg-transparent hover:bg-white/10 rounded-full flex items-center justify-center"
          title={localFavoriteState ? "Remove from favorites" : "Add to favorites"}
          onClick={handleFavoriteClick}
        >
          <span className={`material-symbols-outlined text-2xl ${localFavoriteState ? 'text-yellow-400' : 'text-yellow-400/50'}`}>
            {localFavoriteState ? 'star' : 'star_outline'}
          </span>
        </button>
        <button 
          className="w-10 h-10 bg-transparent hover:bg-white/10 rounded-full flex items-center justify-center"
          title="Block for this meal"
          onClick={handleBlockClick}
        >
          <span className="material-symbols-outlined text-red-400 text-2xl">
            delete
          </span>
        </button>
      </div>
    </div>
  );
};

export default RestaurantView; 